import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, gbp, num, dateTime } from "@/lib/api";
import { AppShell, PageHeader, Panel, SkeletonRows, EmptyState, Tag } from "@/components/Shell";

const TABS = [
  { id: "valuation", label: "Stock valuation" },
  { id: "sales_value", label: "Sales value" },
  { id: "write_offs", label: "Write-offs" },
  { id: "movements", label: "Movement detail" },
  { id: "reorder", label: "Reorder" },
];

export default function Reports() {
  const [tab, setTab] = useState("valuation");
  const [valuation, setValuation] = useState(null);
  const [movements, setMovements] = useState(null);
  const [reorder, setReorder] = useState(null);
  const [salesValue, setSalesValue] = useState(null);
  const [writeOffs, setWriteOffs] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetchers = {
      valuation: () => api.get("/reports/valuation").then((r) => setValuation(r.data)),
      movements: () => api.get("/reports/movements").then((r) => setMovements(r.data)),
      reorder: () => api.get("/reports/reorder").then((r) => setReorder(r.data)),
      sales_value: () => api.get("/reports/sales-value").then((r) => setSalesValue(r.data)),
      write_offs: () => api.get("/reports/write-offs").then((r) => setWriteOffs(r.data)),
    };
    fetchers[tab]().catch((e) => toast.error(apiError(e)));
  }, [tab]);

  return (
    <AppShell>
      <PageHeader title="Reports" subtitle="GBP-only figures at moving-average cost, straight from the movements ledger." testId="reports-header" />

      <div className="mb-4 flex gap-px border border-slate-200 bg-slate-200" data-testid="report-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`report-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 px-4 py-2 text-sm transition-colors ${tab === t.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:text-slate-900"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "valuation" && (
        <Panel
          title="Stock valuation"
          testId="valuation-panel"
          right={valuation && <span className="num text-sm font-medium" data-testid="valuation-total">{gbp(valuation.total_pence)}</span>}
        >
          {!valuation ? <SkeletonRows /> : valuation.rows.length === 0 ? (
            <EmptyState title="Nothing to value" body="Upload opening stock first." testId="valuation-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">SKU</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-right">On hand</th>
                    <th className="px-3 py-2 text-right">Available</th>
                    <th className="px-3 py-2 text-right">Unit cost</th>
                    <th className="px-3 py-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {valuation.rows.map((r) => (
                    <tr key={r.sku} className="hover:bg-slate-50" data-testid={`valuation-row-${r.sku}`}>
                      <td className="mono px-4 py-2">{r.sku}</td>
                      <td className="px-3 py-2 text-slate-700">{r.name}</td>
                      <td className="num px-3 py-2 text-right">{num(r.on_hand)}</td>
                      <td className="num px-3 py-2 text-right">{num(r.available)}</td>
                      <td className="num px-3 py-2 text-right">{gbp(r.unit_cost_pence)}</td>
                      <td className="num px-3 py-2 text-right font-medium">{gbp(r.value_pence)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === "sales_value" && (
        <>
          {salesValue && (
            <div className="mb-4 grid gap-4 sm:grid-cols-4" data-testid="sales-value-totals">
              {[
                ["Cost of goods issued", gbp(salesValue.totals.cost_value_pence), "sales-value-cost"],
                ["Sales value", gbp(salesValue.totals.sale_value_pence), "sales-value-revenue"],
                ["Margin", gbp(salesValue.totals.margin_pence), "sales-value-margin"],
                ["Still committed (at cost)", gbp(salesValue.totals.committed_cost_pence), "sales-value-committed"],
              ].map(([label, value, testId]) => (
                <div key={label} className="border border-slate-200 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
                  <p className="num mt-1 text-xl font-medium" data-testid={testId}>{value}</p>
                  {label === "Margin" && salesValue.totals.margin_pct !== null && (
                    <p className="text-xs text-slate-500">{salesValue.totals.margin_pct}% overall</p>
                  )}
                </div>
              ))}
            </div>
          )}
          <Panel title="Sales value — what each sale is worth at cost" testId="sales-value-panel">
            {!salesValue ? <SkeletonRows /> : salesValue.rows.length === 0 ? (
              <EmptyState title="No sales yet" body="Commit a sale or authorise a Xero invoice and its value shows here." testId="sales-value-empty" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left">Reference</th>
                      <th className="px-3 py-2 text-left">Customer</th>
                      <th className="px-3 py-2 text-left">Source</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                      <th className="px-3 py-2 text-right">Sale value</th>
                      <th className="px-3 py-2 text-right">Margin</th>
                      <th className="px-3 py-2 text-right">Margin %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {salesValue.rows.map((r) => (
                      <Fragment key={r.reference}>
                        <tr className="cursor-pointer hover:bg-slate-50"
                            onClick={() => setExpanded(expanded === r.reference ? null : r.reference)}
                            data-testid={`sales-value-row-${r.reference}`}>
                          <td className="mono px-4 py-2">{r.reference}</td>
                          <td className="px-3 py-2 text-slate-700">{r.customer_name || "—"}</td>
                          <td className="px-3 py-2"><Tag tone={r.source === "xero" ? "blue" : "slate"}>{r.source}</Tag></td>
                          <td className="px-3 py-2 text-xs text-slate-500">{(r.status || "").replace("_", " ")}</td>
                          <td className="num px-3 py-2 text-right">{gbp(r.cost_value_pence)}</td>
                          <td className="num px-3 py-2 text-right">{gbp(r.sale_value_pence)}</td>
                          <td className="num px-3 py-2 text-right" style={{ color: r.margin_pence < 0 ? "#EF4444" : "#10B981" }}>
                            {gbp(r.margin_pence)}
                          </td>
                          <td className="num px-3 py-2 text-right">{r.margin_pct === null ? "—" : `${r.margin_pct}%`}</td>
                        </tr>
                        {expanded === r.reference && (
                          <tr className="bg-slate-50">
                            <td colSpan={8} className="px-4 py-3">
                              <table className="w-full text-xs" data-testid={`sales-value-lines-${r.reference}`}>
                                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                                  <tr>
                                    <th className="py-1 text-left">SKU</th>
                                    <th className="py-1 text-right">Ordered</th>
                                    <th className="py-1 text-right">Issued</th>
                                    <th className="py-1 text-right">Unit cost</th>
                                    <th className="py-1 text-right">Unit price</th>
                                    <th className="py-1 text-right">Cost value</th>
                                    <th className="py-1 text-right">Sale value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.lines.map((l) => (
                                    <tr key={l.sku}>
                                      <td className="mono py-1">{l.sku}</td>
                                      <td className="num py-1 text-right">{num(l.quantity)}</td>
                                      <td className="num py-1 text-right">{num(l.issued)}</td>
                                      <td className="num py-1 text-right">{gbp(l.unit_cost_pence)}</td>
                                      <td className="num py-1 text-right">{gbp(l.unit_price_pence)}</td>
                                      <td className="num py-1 text-right">{gbp(l.cost_value_pence)}</td>
                                      <td className="num py-1 text-right">{gbp(l.sale_value_pence)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}

      {tab === "write_offs" && (
        <>
          {writeOffs && (
            <div className="mb-4 grid gap-4 sm:grid-cols-3" data-testid="write-off-totals">
              {[
                ["Written off", gbp(writeOffs.totals.written_off_pence), `${num(writeOffs.totals.units_lost)} units`, "write-off-total"],
                ["Found / gains", gbp(writeOffs.totals.found_pence), `${num(writeOffs.totals.units_found)} units`, "write-off-found"],
                ["Net effect", gbp(writeOffs.totals.net_pence), "at moving-average cost", "write-off-net"],
              ].map(([label, value, sub, testId]) => (
                <div key={label} className="border border-slate-200 bg-white p-4">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
                  <p className="num mt-1 text-xl font-medium" data-testid={testId}>{value}</p>
                  <p className="text-xs text-slate-500">{sub}</p>
                </div>
              ))}
            </div>
          )}
          <Panel title="Write-offs by reason code" testId="write-offs-panel">
            {!writeOffs ? <SkeletonRows /> : writeOffs.rows.length === 0 ? (
              <EmptyState title="No adjustments recorded" body="Stocktake differences and damaged/lost adjustments appear here with their value." testId="write-offs-empty" />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-right">Adjustments</th>
                    <th className="px-3 py-2 text-right">SKUs</th>
                    <th className="px-3 py-2 text-right">Units lost</th>
                    <th className="px-3 py-2 text-right">Units found</th>
                    <th className="px-3 py-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {writeOffs.rows.map((r) => (
                    <tr key={r.reason} className="hover:bg-slate-50" data-testid={`write-off-row-${r.reason}`}>
                      <td className="px-4 py-2">{r.reason.replace(/_/g, " ")}</td>
                      <td className="num px-3 py-2 text-right">{num(r.movements)}</td>
                      <td className="num px-3 py-2 text-right">{num(r.skus)}</td>
                      <td className="num px-3 py-2 text-right text-red-600">{num(r.units_lost)}</td>
                      <td className="num px-3 py-2 text-right text-emerald-600">{num(r.units_found)}</td>
                      <td className="num px-3 py-2 text-right font-medium" style={{ color: r.value_pence < 0 ? "#EF4444" : "#10B981" }}>
                        {gbp(r.value_pence)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          {writeOffs?.recent?.length > 0 && (
            <Panel title="Recent adjustments" testId="write-offs-recent-panel" className="mt-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">SKU</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2 text-left">Note</th>
                    <th className="px-3 py-2 text-left">By</th>
                    <th className="px-3 py-2 text-left">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {writeOffs.recent.map((m, i) => (
                    <tr key={i} data-testid={`write-off-recent-${i}`}>
                      <td className="mono px-4 py-2">{m.sku}</td>
                      <td className="px-3 py-2 text-xs">{m.reason.replace(/_/g, " ")}</td>
                      <td className="num px-3 py-2 text-right">{m.quantity > 0 ? `+${m.quantity}` : m.quantity}</td>
                      <td className="num px-3 py-2 text-right">{gbp(m.value_pence)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{m.note}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{m.user_email}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{dateTime(m.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
        </>
      )}

      {tab === "movements" && (
        <Panel title="Movement detail" testId="movements-panel">
          {!movements ? <SkeletonRows /> : movements.length === 0 ? (
            <EmptyState title="No movements" body="Stock events appear here as soon as anything moves." testId="movements-report-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">SKU</th>
                    <th className="px-3 py-2 text-left">Event</th>
                    <th className="px-3 py-2 text-right">On hand Δ</th>
                    <th className="px-3 py-2 text-right">Committed Δ</th>
                    <th className="px-3 py-2 text-right">Incoming Δ</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">User</th>
                    <th className="px-3 py-2 text-left">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {movements.map((m, i) => (
                    <tr key={i} className="hover:bg-slate-50" data-testid={`movement-report-row-${i}`}>
                      <td className="mono px-4 py-2">{m.sku}</td>
                      <td className="px-3 py-2"><Tag>{m.type.replace(/[:_]/g, " ")}</Tag></td>
                      <td className="num px-3 py-2 text-right">{m.qty_on_hand || "—"}</td>
                      <td className="num px-3 py-2 text-right">{m.qty_committed || "—"}</td>
                      <td className="num px-3 py-2 text-right">{m.qty_incoming || "—"}</td>
                      <td className="num px-3 py-2 text-right">{num(m.balance_on_hand)}</td>
                      <td className="mono px-3 py-2 text-xs text-slate-500">{m.reference || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{m.user_email}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{dateTime(m.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {tab === "reorder" && (
        <Panel title="Reorder — Available + Incoming below reorder point" testId="reorder-panel">
          {!reorder ? <SkeletonRows /> : reorder.length === 0 ? (
            <EmptyState title="Nothing to reorder" body="Available plus incoming covers every reorder point." testId="reorder-empty" />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-right">Available</th>
                  <th className="px-3 py-2 text-right">Incoming</th>
                  <th className="px-3 py-2 text-right">Reorder point</th>
                  <th className="px-3 py-2 text-right">Suggested qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reorder.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`reorder-row-${r.sku}`}>
                    <td className="mono px-4 py-2">{r.sku}</td>
                    <td className="px-3 py-2 text-slate-700">{r.name}</td>
                    <td className="num px-3 py-2 text-right" style={{ color: r.available < 0 ? "#EF4444" : "#10B981" }}>{num(r.available)}</td>
                    <td className="num px-3 py-2 text-right text-violet-600">{num(r.incoming)}</td>
                    <td className="num px-3 py-2 text-right">{num(r.reorder_point)}</td>
                    <td className="num px-3 py-2 text-right font-medium">{num(r.suggested_qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}
    </AppShell>
  );
}
